import { ApprovalableTypeEnum } from 'api-hooks/common';
import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { ApprovalItemWorkflowEnum } from 'api-hooks/sale/common';
import {
  ActionStatusEnum,
  TravelDocumentSalesModel,
  useUpdateStatusTravelDocumentSales,
} from 'api-hooks/sale/travel-document';
import { CheckCircleSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
import { DocumentSaleStatusEnum } from 'common/constants/enum';
import notification from 'components/common/notification';
import Button from 'components/elements/buttons/button';
import { PopoverMenuType } from 'components/popover';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import RequestApprovalButton from 'legacy-modules/approval/approval/components/request-approval-button';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import useCheckCompletenessDocument from './use-check-completeness-document';
import CompletionEstimateDialog from '../components/completion-estimate-dialog';

export default function useDocumentSaleUpdateStatus(
  data: TravelDocumentSalesModel,
) {
  const isAllDocumentComplete = useCheckCompletenessDocument(data);
  const { t } = useTranslation();
  const updateStatusTravelMutate = useUpdateStatusTravelDocumentSales();
  const dialog = useDialog();
  const onDismiss = useDismiss('document-sales');
  const { can } = useAuthorization();

  const requestApprovalRevert = React.useMemo(() => {
    switch (data.documentSaleStatus) {
      case DocumentSaleStatusEnum.RevertToSubmittedDraft:
      case DocumentSaleStatusEnum.RevertToProcessedDraft:
        return true;
      default:
        return false;
    }
  }, [data.documentSaleStatus]);

  //revert metadata
  const revertStatus = React.useMemo(() => {
    if (data.documentSaleStatus === DocumentSaleStatusEnum.Finished) {
      return {
        condition: true,
        text: t('status:draft_revert_to_submitted'),
      };
    }
    if (data.documentSaleStatus === DocumentSaleStatusEnum.Submitted) {
      return {
        condition: true,
        text: t('status:draft_revert_to_processed'),
      };
    }
    return { condition: false, text: '' };
  }, [t, data.documentSaleStatus]);

  // disabled button status
  const isDisabledButtonStatus = React.useMemo(() => {
    switch (data.documentSales[0].documentType) {
      case DocumentTypeEnum.passport:
        if (data.documentSaleStatus === DocumentSaleStatusEnum.Processed) {
          return !isAllDocumentComplete;
        }
        return false;
      default:
        if (data.documentSaleStatus === DocumentSaleStatusEnum.Submitted) {
          return !isAllDocumentComplete;
        }
        return false;
    }
  }, [isAllDocumentComplete, data]);

  // update status dialog
  const onUpdateStatusDialog = React.useCallback(
    (action, completionDateEstimation, onAction?: () => void) => {
      dialog.showConfirmation({
        message: t('common:confirmation_text'),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            const result = await updateStatusTravelMutate.mutateAsync({
              id: data.id,
              body: { action, completionDateEstimation },
            });

            onAction?.();
            notification.success({ message: result?.message });
            onDismiss();
          } catch (error) {
            notification.error({ message: error.message });
          } finally {
            onClose();
          }
        },

        onNegativeAction: (onClose) => {
          onClose();
        },
      });
    },
    [data.id, dialog, onDismiss, t, updateStatusTravelMutate],
  );

  const onUpdateStatus = React.useCallback(
    async (status: string) => {
      let action = '';

      switch (status) {
        case DocumentSaleStatusEnum.Submitted:
          action = ActionStatusEnum.SUBMIT;
          break;
        case DocumentSaleStatusEnum.Finished:
          action = ActionStatusEnum.FINISH;
          break;
        default:
          action = status;
          break;
      }

      if (status === DocumentSaleStatusEnum.Submitted) {
        dialog.showCustom({
          render: (dismiss) => (
            <CompletionEstimateDialog
              onClose={dismiss}
              onSubmit={(val) => {
                dismiss();
                onUpdateStatusDialog(action, val.completionDateEstimation);
              }}
            />
          ),
        });
        return;
      }

      onUpdateStatusDialog(action, undefined);
    },
    [dialog, onUpdateStatusDialog],
  );

  const nextDocumentStatus = React.useMemo(() => {
    let status = '';

    switch (data.documentSaleStatus) {
      case DocumentSaleStatusEnum.Processed:
        if (data.documentSales[0].documentType === DocumentTypeEnum.passport) {
          status = DocumentSaleStatusEnum.Finished;
        } else {
          status = DocumentSaleStatusEnum.Submitted;
        }
        break;
      case DocumentSaleStatusEnum.Submitted:
        status = DocumentSaleStatusEnum.Finished;
        break;
      case DocumentSaleStatusEnum.Finished:
        status = DocumentSaleStatusEnum.Finished;
        break;
    }
    return status;
  }, [data]);

  const previousDocumentStatus = React.useMemo(() => {
    let status = '';

    switch (data.documentSaleStatus) {
      case DocumentSaleStatusEnum.Submitted:
        status = ActionStatusEnum.REVERT_TO_PROCESSED;
        break;
      case DocumentSaleStatusEnum.Finished:
        if (data.documentSales[0].documentType === DocumentTypeEnum.passport) {
          status = ActionStatusEnum.REVERT_TO_PROCESSED;
        }
        status = ActionStatusEnum.REVERT_TO_SUBMITTED;
        break;
    }

    return status;
  }, [data]);

  const updateDocumentStatusAuthorization =
    AuthorizationRules.DocumentSalesUpdateDocumentStatus;

  const updateNextDocumentStatusPermissions =
    (data.documentSaleStatus === DocumentSaleStatusEnum.Processed ||
      data.documentSaleStatus === DocumentSaleStatusEnum.Submitted) &&
    can(updateDocumentStatusAuthorization);

  const updateBackDocumentStatusPermission =
    revertStatus.condition && can(updateDocumentStatusAuthorization);

  const onUpdateNextStatus = React.useCallback(async () => {
    await onUpdateStatus(nextDocumentStatus);
  }, [nextDocumentStatus, onUpdateStatus]);

  const onUpdatePreviousStatus = React.useCallback(() => {
    onUpdateStatusDialog(previousDocumentStatus, undefined, () =>
      dialog.showCustom({
        render(onClose) {
          return (
            <RequestApprovalModal
              onClose={onClose}
              data={{
                approvalableId: data.id,
                approvalableType: ApprovalableTypeEnum.Sale,
                workflow: ApprovalItemWorkflowEnum.document_sale,
              }}
              onDismissSuccess={onDismiss}
            />
          );
        },
      }),
    );
  }, [
    data.id,
    dialog,
    onDismiss,
    onUpdateStatusDialog,
    previousDocumentStatus,
  ]);

  /** Dari meeting 12 Apr 2024, Revert status harus minta approval, dan Draft tidak perlu approval */
  const onRevert = React.useCallback(async () => {
    dialog.showConfirmation({
      message: t('common:revert_status'),
      title: t('common:revert_status_description'),
      onPositiveAction: async (onClose) => {
        try {
          onClose();
          await onUpdateStatus(previousDocumentStatus);

          dialog.showCustom({
            render: (onClose) => (
              <RequestApprovalModal
                onClose={onClose}
                data={{
                  approvalableId: data.id,
                  approvalableType: ApprovalableTypeEnum.Sale,
                  workflow: ApprovalItemWorkflowEnum.document_sale,
                }}
                onDismissSuccess={onDismiss}
              />
            ),
          });
        } catch (error) {
          notification.error({ message: error.message });
        } finally {
          onDismiss();
        }
      },
      onNegativeAction: (dismiss) => {
        dismiss();
      },
    });
  }, [data.id, dialog, onDismiss, onUpdateStatus, previousDocumentStatus, t]);

  const revertStatusAction: PopoverMenuType = {
    key: `revert-${data.id}`,
    label: revertStatus.text,
    onClick: onUpdatePreviousStatus,
    isError: true,
    isAllowed: updateBackDocumentStatusPermission,
  };

  const nextStatusAction: PopoverMenuType = {
    key: `next-${data.id}`,
    label: t(`status:${nextDocumentStatus}`),
    onClick: onUpdateNextStatus,
    isError: false,
    isAllowed: updateNextDocumentStatusPermissions,
  };

  const updateNextDocumentComponent = updateNextDocumentStatusPermissions && (
    <Button
      startEnhancer={(color, size) => (
        <CheckCircleSVG color={color} size={size} />
      )}
      onClick={onUpdateNextStatus}
      disabled={isDisabledButtonStatus}
    >
      {t(`status:${nextDocumentStatus}`)}
    </Button>
  );

  const requestApprovalRevertDraftButton = requestApprovalRevert && (
    <RequestApprovalButton
      data={{
        approvalableId: data.id,
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.document_sale,
      }}
      onDismiss={onDismiss}
    />
  );

  return {
    updateBackDocumentStatusPermission,
    updateNextDocumentComponent,
    nextStatusAction,
    revertStatusAction,
    onUpdateNextStatus,
    onUpdatePreviousStatus,
    updateNextDocumentStatusPermissions,
    onUpdateStatus,
    onUpdateStatusDialog,
    onRevert,
    requestApprovalRevertDraftButton,
  };
}
